// File: src/Components/payments/ChargesDetailsPanel.jsx
// ---------------------------------------------------------------------------
// Rich, detailed billing-calculation breakdown (ported from xtram manager
// dashboard's ChargesDetailsTab). Renders per-month accordions with:
//   • Summary tiles (charges / one-time / refunds / discounts / payments / net)
//   • Plan-charge rollup chips
//   • Aggregated charge lines
//   • Upload-overage detail (average-uploads gate + per-client excess)
//   • Per-client cycles (accountant-payer / client-payer) with line drilldown
//   • Payments / refunds / one-time-charges lists
//   • Grand totals
// Accepts the payload returned by PaymentService.{accountant,client}.getCalculation.
// ---------------------------------------------------------------------------

import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box, Typography, Chip, Paper, Grid, Tab, Tabs, Alert,
  Table as MuiTable, TableBody, TableCell, TableContainer, TableHead, TableRow, TablePagination,
  Accordion, AccordionSummary, AccordionDetails
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CalculateIcon from '@mui/icons-material/Calculate';

/* ───────────────────────── helpers (camel/Pascal tolerant) ───────────────────────── */
const pick = (obj, key) => {
  if (obj == null) return undefined;
  const lower = key.charAt(0).toLowerCase() + key.slice(1);
  const upper = key.charAt(0).toUpperCase() + key.slice(1);
  return obj[lower] ?? obj[upper];
};

const formatCurrency = (amount, fallback = '—') => {
  if (amount == null || isNaN(Number(amount))) return fallback;
  return new Intl.NumberFormat('he-IL', {
    style: 'currency', currency: 'ILS', minimumFractionDigits: 2, maximumFractionDigits: 2
  }).format(Number(amount));
};

const formatMonthKey = (monthKey) => {
  if (!monthKey || String(monthKey).length < 7) return monthKey || '—';
  const [year, month] = String(monthKey).split('-');
  return `${month}/${year}`;
};

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  try {
    return new Date(dateStr).toLocaleDateString('he-IL', { day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch {
    return dateStr;
  }
};

const getNetBalanceColor = (amount) => {
  const n = Number(amount);
  if (n > 0) return '#d32f2f';
  if (n < 0) return '#2e7d32';
  return 'inherit';
};

const PAYMENT_METHOD_MAP = {
  BankTransfer: 'העברה בנקאית', CreditCard: 'כרטיס אשראי', Cash: 'מזומן',
  PayPal: 'PayPal', Check: "צ'ק", Other: 'אחר', RecurringAutomatic: 'חיוב אוטומטי'
};
const translatePaymentMethod = (m) => PAYMENT_METHOD_MAP[m] || m || '—';

const LINE_TYPE_MAP = {
  Plan: 'תוכנית', PLAN: 'תוכנית',
  ExtraUploads: 'העלאות חריגות', EXTRA_UPLOADS: 'העלאות חריגות',
  ExtraClients: 'לקוחות חריגים', EXTRA_CLIENTS: 'לקוחות חריגים',
  Adjustment: 'התאמה', ADJUSTMENT: 'התאמה',
  DigitalSignature: 'חתימה דיגיטלית', DIGITAL_SIGNATURE: 'חתימה דיגיטלית',
  CreditTerminalManageFee: 'דמי ניהול מסוף אשראי', CREDIT_TERMINAL_MANAGE_FEE: 'דמי ניהול מסוף אשראי',
  CreditTerminalTxFee: 'עמלת עסקה מסוף אשראי', CREDIT_TERMINAL_TX_FEE: 'עמלת עסקה מסוף אשראי',
  CreditTerminalServiceFee: 'שירות חודשי מסוף אשראי', CREDIT_TERMINAL_SERVICE_FEE: 'שירות חודשי מסוף אשראי',
  CreditTerminalSetupFee: 'עלות הקמה מסוף אשראי', CREDIT_TERMINAL_SETUP_FEE: 'עלות הקמה מסוף אשראי',
  // Provider-neutral: this line type carries the clearing fee for both Tranzila and Grow.
  CreditTerminalTranzilaFee: 'עמלת סליקה', CREDIT_TERMINAL_TRANZILA_FEE: 'עמלת סליקה'
};
const translateLineType = (type) => LINE_TYPE_MAP[type] || type || '—';

/** Roll up aggregate cycle lines for the plan-charge summary chips. */
function summarizeAggregatedBillingLines(lines = []) {
  const amount = (l) => Number(pick(l, 'lineTotal') ?? 0) || 0;
  const description = (l) => String(pick(l, 'description') ?? '');
  const lineType = (l) => String(pick(l, 'lineTypeString') ?? pick(l, 'lineType') ?? '');
  const sumWhere = (pred) => (lines || []).filter(pred).reduce((acc, l) => acc + amount(l), 0);
  const isCT = (key, heb) => (l) =>
    lineType(l).includes(key) || description(l).includes(heb);
  return {
    inactive: sumWhere((l) => description(l).includes('לא פעיל')),
    manageOnly: sumWhere((l) => description(l).includes('ניהול בלבד')),
    premium: sumWhere((l) => description(l).includes('פרימיום')),
    creditTerminalManageFee: sumWhere(isCT('CreditTerminalManageFee', 'דמי ניהול מסוף')),
    creditTerminalTxFee: sumWhere(isCT('CreditTerminalTxFee', 'עמלת עסקה מסוף')),
    creditTerminalServiceFee: sumWhere(isCT('CreditTerminalServiceFee', 'שירות חודשי מסוף')),
    creditTerminalSetupFee: sumWhere(isCT('CreditTerminalSetupFee', 'עלות הקמה')),
    creditTerminalTranzilaFee: sumWhere((l) =>
      isCT('CreditTerminalTranzilaFee', 'עמלת טרנזילה')(l) || description(l).includes('עמלת סליקה')),
    monthlyPlan: sumWhere((l) => description(l).includes('חיוב תוכנית חודשית'))
  };
}

const createdByText = (r) =>
  pick(r, 'createdViaSystem')
    ? 'מערכת'
    : (pick(r, 'createdByUserType') && pick(r, 'createdByUserId')
      ? `${pick(r, 'createdByUserType')}: ${pick(r, 'createdByUserId')}`
      : '-');

const cellSx = { py: 0.3, px: 0.75, fontSize: 11, whiteSpace: 'nowrap' };
const headerSx = { ...cellSx, fontWeight: 700, fontSize: 11 };

/* ───────────────────────── small building blocks ───────────────────────── */
const SummaryTile = ({ label, value, color }) => (
  <Paper elevation={0} sx={{ p: 1, textAlign: 'center', flex: 1, minWidth: 80, border: '1px solid #e0e0e0', borderRadius: 1 }}>
    <Typography variant="caption" sx={{ fontSize: 10, color: '#666', display: 'block', lineHeight: 1.2 }}>{label}</Typography>
    <Typography variant="body2" sx={{ fontWeight: 700, fontSize: 13, color: color || 'inherit', direction: 'ltr' }}>
      {formatCurrency(value)}
    </Typography>
  </Paper>
);

const CompactTable = ({ rows, columns, title, pageSize = 10, rowsPerPageOptions = [10, 25, 50, 100] }) => {
  const [page, setPage] = useState(0);
  const [rpp, setRpp] = useState(pageSize);
  if (!rows?.length) return null;
  const paged = rows.slice(page * rpp, page * rpp + rpp);
  return (
    <Box>
      {title && <Typography variant="subtitle2" sx={{ fontSize: 11, fontWeight: 700, mb: 0.5, color: '#444' }}>{title}</Typography>}
      <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto' }}>
        <MuiTable size="small" stickyHeader>
          <TableHead>
            <TableRow>
              {columns.map((col, i) => (
                <TableCell key={i} sx={headerSx} align={col.align || 'left'}>{col.label}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {paged.map((row, i) => (
              <TableRow key={i} hover sx={{ '&:last-child td': { borderBottom: 0 } }}>
                {columns.map((col, j) => (
                  <TableCell key={j} sx={cellSx} align={col.align || 'left'}>
                    {col.render ? col.render(row) : pick(row, col.field)}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </MuiTable>
        {rows.length > rpp && (
          <TablePagination
            component="div"
            count={rows.length}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            rowsPerPage={rpp}
            onRowsPerPageChange={(e) => { setRpp(+e.target.value); setPage(0); }}
            rowsPerPageOptions={rowsPerPageOptions}
            sx={{ '& .MuiTablePagination-toolbar': { minHeight: 32 }, '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': { fontSize: 11 } }}
          />
        )}
      </TableContainer>
    </Box>
  );
};

const PlanChargeSummary = ({ lines }) => {
  const rollup = useMemo(() => summarizeAggregatedBillingLines(lines), [lines]);
  const items = [
    { key: 'monthlyPlan', label: 'תוכנית חודשית', value: rollup.monthlyPlan },
    { key: 'premium', label: 'פרימיום', value: rollup.premium },
    { key: 'manageOnly', label: 'ניהול בלבד', value: rollup.manageOnly },
    { key: 'inactive', label: 'עסקים לא פעילים', value: rollup.inactive },
    { key: 'creditTerminalManageFee', label: 'דמי ניהול מסוף אשראי', value: rollup.creditTerminalManageFee },
    { key: 'creditTerminalTxFee', label: 'עמלת עסקה מסוף אשראי', value: rollup.creditTerminalTxFee },
    { key: 'creditTerminalServiceFee', label: 'שירות חודשי מסוף אשראי', value: rollup.creditTerminalServiceFee },
    { key: 'creditTerminalSetupFee', label: 'עלות הקמה מסוף אשראי', value: rollup.creditTerminalSetupFee },
    { key: 'creditTerminalTranzilaFee', label: 'עמלת סליקה', value: rollup.creditTerminalTranzilaFee }
  ].filter((item) => item.value > 0);
  if (!items.length) return null;
  return (
    <Box sx={{ mb: 1.5 }}>
      <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.75, color: '#444' }}>סיכום חיובי תוכנית</Typography>
      <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
        {items.map((item) => <SummaryTile key={item.key} label={item.label} value={item.value} />)}
      </Box>
    </Box>
  );
};

/* ───────────────────────── column definitions ───────────────────────── */
const lineColumns = [
  { label: 'תיאור', render: (r) => pick(r, 'description') || '—' },
  { label: 'סוג', render: (r) => translateLineType(pick(r, 'lineTypeString')) },
  { label: 'כמות', render: (r) => pick(r, 'quantity') ?? '—', align: 'center' },
  { label: 'מחיר', render: (r) => formatCurrency(pick(r, 'unitPrice')), align: 'left' },
  { label: 'סה״כ', render: (r) => formatCurrency(pick(r, 'lineTotal')), align: 'left' }
];

const uploadOverageColumns = [
  {
    label: 'שם',
    render: (r) => {
      const name = pick(r, 'clientName');
      return name ? <Typography sx={{ fontSize: 11, fontWeight: 600, whiteSpace: 'normal' }}>{name}</Typography> : '—';
    }
  },
  { label: 'מזהה תיק', render: (r) => pick(r, 'clientCaseId') ?? '—' },
  { label: 'העלאות', render: (r) => pick(r, 'uploadCount') ?? 0, align: 'center' },
  { label: 'מכסה לעסק', render: (r) => pick(r, 'maxUploadsLimit') ?? '—', align: 'center' },
  { label: 'חריגה ממכסה', render: (r) => (pick(r, 'excessOverPerClientLimit') > 0 ? pick(r, 'excessOverPerClientLimit') : '—'), align: 'center' }
];

const paymentColumns = [
  { label: 'תאריך', render: (r) => formatDate(pick(r, 'paymentDate')) },
  { label: 'אמצעי', render: (r) => translatePaymentMethod(pick(r, 'paymentMethodString')) },
  { label: 'סכום', render: (r) => formatCurrency(pick(r, 'amount')), align: 'left' },
  { label: '', render: (r) => (pick(r, 'isConfirmed') ? '✓' : '✗'), align: 'center' },
  { label: 'נוצר על ידי', render: createdByText }
];

const refundColumns = [
  { label: 'תאריך', render: (r) => formatDate(pick(r, 'refundDate')) },
  { label: 'סכום', render: (r) => formatCurrency(pick(r, 'amount')), align: 'left' },
  { label: 'נוצר על ידי', render: createdByText }
];

const otcColumns = [
  { label: 'תיאור', render: (r) => pick(r, 'description') || '—' },
  { label: 'תאריך', render: (r) => formatDate(pick(r, 'chargeDate')) },
  { label: 'סכום', render: (r) => formatCurrency(pick(r, 'amount')), align: 'left' },
  { label: 'נוצר על ידי', render: createdByText }
];

const discountColumns = [
  { label: 'תיאור', render: (r) => pick(r, 'description') || (pick(r, 'isPercentage') ? `${pick(r, 'discountValue')}%` : formatCurrency(pick(r, 'discountValue'))) },
  { label: 'סכום', render: (r) => formatCurrency(pick(r, 'calculatedAmount')), align: 'left' },
  { label: 'נוצר על ידי', render: createdByText }
];

function clientSummaryNameByCaseId(clientSummaryItems) {
  const map = {};
  (clientSummaryItems || []).forEach((c) => {
    const id = pick(c, 'clientCaseId');
    if (id) map[id] = pick(c, 'name') ?? '';
  });
  return map;
}

function buildClientCycleColumnsWithNames(clientSummaryItems) {
  const byId = clientSummaryNameByCaseId(clientSummaryItems);
  return [
    { label: 'מזהה', render: (r) => pick(r, 'clientCaseId') ?? '—' },
    {
      label: 'שם',
      render: (r) => {
        const id = pick(r, 'clientCaseId');
        const fromMonth = id ? byId[id] : '';
        return fromMonth || pick(r, 'metadata')?.name || pick(r, 'clientName') || '—';
      }
    },
    { label: 'תיאור', render: (r) => (pick(r, 'lines') || pick(r, 'Lines') || [])[0]?.description || '—' },
    { label: 'סכום', render: (r) => formatCurrency(pick(r, 'totalCharges')), align: 'left' }
  ];
}

/* ───────────────────────── per-month client counts ───────────────────────── */
function monthAccPaysCount(month) {
  const agg = pick(month, 'aggregatedCycle');
  const n = pick(agg, 'totalClientsAccountantPays');
  const arr = pick(month, 'clientCyclesWhereAccountantIsPayer');
  const len = Array.isArray(arr) ? arr.length : 0;
  return (n != null ? Number(n) : len) || len;
}
function monthCliPaysCount(month) {
  const agg = pick(month, 'aggregatedCycle');
  const n = pick(agg, 'totalClientsClientPays');
  const arr = pick(month, 'clientCyclesWhereClientIsPayer');
  const len = Array.isArray(arr) ? arr.length : 0;
  return (n != null ? Number(n) : len) || len;
}
function monthTotalClientsCount(month) {
  return monthAccPaysCount(month) + monthCliPaysCount(month);
}

/* ───────────────────────── status chips ───────────────────────── */
const StatusChips = ({ month, height = 20, fontSize = 10 }) => (
  <>
    {pick(month, 'isSavedCycle') && <Chip icon={<CheckCircleIcon sx={{ fontSize: 12 }} />} label="נשמר" size="small" color="success" variant="outlined" sx={{ height, fontSize }} />}
    {pick(month, 'isCalculated') && <Chip icon={<CalculateIcon sx={{ fontSize: 12 }} />} label="מחושב" size="small" color="info" variant="outlined" sx={{ height, fontSize }} />}
  </>
);

/* ───────────────────────── accountant/client-payer month accordion ───────────────────────── */
const MonthAccordion = ({ month, showClientPayerSection = true, showClientCounts = true }) => {
  const aggregatedCycle = pick(month, 'aggregatedCycle');
  const clientCyclesAcc = pick(month, 'clientCyclesWhereAccountantIsPayer');
  const clientCyclesCli = pick(month, 'clientCyclesWhereClientIsPayer');
  const paymentsList = pick(month, 'paymentsList');
  const refundsList = pick(month, 'refundsList');
  const oneTimeChargesList = pick(month, 'oneTimeChargesList');

  const accPaysCount = monthAccPaysCount(month);
  const cliPaysCount = monthCliPaysCount(month);
  const totalCount = monthTotalClientsCount(month);

  const aggLines = pick(aggregatedCycle, 'lines') || [];
  const discountLines = pick(aggregatedCycle, 'discountLines') || [];

  const hasAccCycles = clientCyclesAcc?.length > 0;
  const hasCliCycles = showClientPayerSection && clientCyclesCli?.length > 0;
  const hasPayments = paymentsList?.length > 0;
  const hasRefunds = refundsList?.length > 0;
  const hasOtc = oneTimeChargesList?.length > 0;
  const hasDiscounts = discountLines.length > 0;

  const accCycleCols = buildClientCycleColumnsWithNames(pick(month, 'clientsWhereAccountantPays'));
  const cliCycleCols = buildClientCycleColumnsWithNames(pick(month, 'clientsWhereClientPays'));

  return (
    <Accordion defaultExpanded={false} sx={{ mb: 0.5, '&:before': { display: 'none' }, '& .MuiAccordionDetails-root': { px: 1.5, pb: 1.5 } }} elevation={1}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 40, '& .MuiAccordionSummary-content': { my: 0.25, alignItems: 'center', gap: 1, flexWrap: 'wrap' } }}>
        <Typography sx={{ fontWeight: 700, fontSize: 13, minWidth: 70 }}>{formatMonthKey(pick(month, 'monthKey'))}</Typography>
        <StatusChips month={month} />
        <Box sx={{ flex: 1 }} />
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', mr: 1, flexWrap: 'wrap' }}>
          {showClientCounts && <Typography sx={{ fontSize: 11, color: '#666' }}>רו״ח משלם: <strong>{accPaysCount}</strong></Typography>}
          {showClientCounts && <Typography sx={{ fontSize: 11, color: '#666' }}>לקוח משלם: <strong>{cliPaysCount}</strong></Typography>}
          {showClientCounts && <Typography sx={{ fontSize: 11, color: '#666' }}>סה״כ: <strong>{totalCount}</strong></Typography>}
          <Typography sx={{ fontSize: 11, color: '#666' }}>חיובים: <strong>{formatCurrency(pick(month, 'charges'))}</strong></Typography>
          <Typography sx={{ fontSize: 11, color: '#666' }}>תשלומים: <strong>{formatCurrency(pick(month, 'payments'))}</strong></Typography>
          <Typography sx={{ fontSize: 11, fontWeight: 700, color: getNetBalanceColor(pick(month, 'netBalance')), direction: 'ltr' }}>
            יתרה: {formatCurrency(pick(month, 'netBalance'))}
          </Typography>
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ pt: 0 }}>
        <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mb: 1.5 }}>
          <SummaryTile label="חיובים" value={pick(month, 'charges')} />
          <SummaryTile label="חד פעמי" value={pick(month, 'oneTimeCharges')} />
          <SummaryTile label="החזרים" value={pick(month, 'refunds')} />
          <SummaryTile label="הנחות" value={pick(month, 'discounts')} />
          <SummaryTile label="תשלומים" value={pick(month, 'payments')} />
          <SummaryTile label="יתרה" value={pick(month, 'netBalance')} color={getNetBalanceColor(pick(month, 'netBalance'))} />
        </Box>

        <Grid container spacing={1.5}>
          {aggLines.length > 0 && (
            <Grid item xs={12} md={hasDiscounts ? 8 : 12}>
              <PlanChargeSummary lines={aggLines} />
              <CompactTable
                rows={aggLines}
                columns={lineColumns}
                title={showClientCounts ? `פירוט חיוב מצטבר (${totalCount} לקוחות)` : 'פירוט חיוב'}
                pageSize={100}
                rowsPerPageOptions={[25, 50, 100, 250]}
              />
            </Grid>
          )}
          {(() => {
            const agg = aggregatedCycle;
            const exceeding = pick(agg, 'clientsExceedingUploadLimit');
            const topPool = pick(agg, 'topUploadClientsForPoolOverage');
            const refMk = pick(agg, 'uploadUsageReferenceMonthKey');
            const maxUp = pick(agg, 'uploadMaxPerClientLimit');
            const avgLimit = pick(agg, 'uploadAverageLimit');
            const avgExceeded = Boolean(pick(agg, 'uploadAverageExceeded'));
            const hasAverage = avgLimit != null;
            const fmtN = (n) => (typeof n === 'number' ? n.toLocaleString('he-IL') : '—');
            const hasExceeding = Array.isArray(exceeding) && exceeding.length > 0;
            const hasTopPool = Array.isArray(topPool) && topPool.length > 0;
            if (!hasExceeding && !hasTopPool && !hasAverage) return null;
            const lines = pick(agg, 'lines') || [];
            const hasExtraUploadsLine = lines.some(
              (l) => pick(l, 'lineTypeString') === 'ExtraUploads' || pick(l, 'lineType') === 1
            );
            return (
              <Grid item xs={12}>
                <Paper variant="outlined" sx={{ p: 1.5, mt: 1, borderColor: '#ed6c02', bgcolor: '#fffaf5' }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: 12, mb: 0.75 }}>
                    {hasExtraUploadsLine
                      ? 'פירוט העלאות (בסיס לשורת «העלאות נוספות» בטבלה למעלה)'
                      : 'פירוט העלאות (חודש ייחוס לחיוב)'}
                  </Typography>
                  {hasAverage && (
                    <Typography variant="caption" sx={{ display: 'block', mb: 1, color: avgExceeded ? '#b45309' : '#304FFF', fontWeight: 700, lineHeight: 1.45 }}>
                      ממוצע העלאות לעסק: {fmtN(pick(agg, 'uploadAveragePerClient'))} (סה״כ {fmtN(pick(agg, 'uploadTotalUsed'))} ÷ {fmtN(pick(agg, 'uploadAverageClientsCount'))} לקוחות משלמים), מגבלת ממוצע {fmtN(avgLimit)}.
                      {avgExceeded
                        ? ' הממוצע עבר את המגבלה, ולכן כל עסק חויב על ההעלאות שמעבר למכסה שלו.'
                        : ' הממוצע לא עבר את המגבלה, ולכן אין חיוב על העלאות, גם לעסקים שחרגו מהמכסה שלהם.'}
                    </Typography>
                  )}
                  {refMk && (
                    <Typography variant="caption" sx={{ display: 'block', mb: 1, color: '#555', lineHeight: 1.45 }}>
                      מספרי העלאות לחודש {formatMonthKey(refMk)} (חודש לפני {formatMonthKey(pick(month, 'monthKey'))}). מכסה לעסק בודד בתכנית: {maxUp ?? '—'}.
                      {' '}
                      הספירה מבוססת על נתוני שימוש חודשיים (מסמכים שעברו OCR); ללקוחות בלי רשומה — שימוש במונה העלאות הישן.
                    </Typography>
                  )}
                  {hasExceeding && (
                    <CompactTable rows={exceeding} columns={uploadOverageColumns} title="עסקים שחרגו ממכסת העלאות לעסק" pageSize={15} />
                  )}
                  {hasTopPool && (
                    <>
                      <Typography variant="caption" sx={{ display: 'block', my: 1, color: '#666', lineHeight: 1.4 }}>
                        הוחל חיוב כולל על חריגה במאזן העלאות (מעל המכסה המשותפת). אף עסק בודד לא חרג ממכסה לעסק — להלן העסקים עם הכי הרבה העלאות באותו חודש:
                      </Typography>
                      <CompactTable rows={topPool} columns={uploadOverageColumns} title="עסקים מובילים לפי מספר העלאות" pageSize={15} />
                    </>
                  )}
                </Paper>
              </Grid>
            );
          })()}
          {hasDiscounts && (
            <Grid item xs={12} md={4}>
              <CompactTable rows={discountLines} columns={discountColumns} title="הנחות" pageSize={10} />
            </Grid>
          )}

          {hasAccCycles && (
            <Grid item xs={12} md={hasCliCycles ? 6 : 12}>
              <CompactTable rows={clientCyclesAcc} columns={accCycleCols} title={`רו״ח משלם (${accPaysCount})`} pageSize={accPaysCount > 25 ? 25 : 10} />
            </Grid>
          )}
          {hasCliCycles && (
            <Grid item xs={12} md={hasAccCycles ? 6 : 12}>
              <CompactTable rows={clientCyclesCli} columns={cliCycleCols} title={`לקוח משלם (${cliPaysCount})`} pageSize={10} />
            </Grid>
          )}

          {hasPayments && (
            <Grid item xs={12} md={hasRefunds || hasOtc ? 4 : 12}>
              <CompactTable rows={paymentsList} columns={paymentColumns} title={`תשלומים (${paymentsList.length})`} pageSize={10} />
            </Grid>
          )}
          {hasRefunds && (
            <Grid item xs={12} md={hasPayments && hasOtc ? 4 : hasPayments || hasOtc ? 6 : 12}>
              <CompactTable rows={refundsList} columns={refundColumns} title={`החזרים (${refundsList.length})`} pageSize={10} />
            </Grid>
          )}
          {hasOtc && (
            <Grid item xs={12} md={hasPayments && hasRefunds ? 4 : hasPayments || hasRefunds ? 6 : 12}>
              <CompactTable rows={oneTimeChargesList} columns={otcColumns} title={`חד פעמי (${oneTimeChargesList.length})`} pageSize={10} />
            </Grid>
          )}
        </Grid>
      </AccordionDetails>
    </Accordion>
  );
};

const CLIENT_DETAIL_INITIAL_EXPANDED = 5;

/* ───────────────────────── client-payer month accordion (from accountant calc) ───────────────────────── */
const ClientPayerMonthAccordion = ({ month }) => {
  const cycles = pick(month, 'clientCyclesWhereClientIsPayer') || [];
  if (cycles.length === 0) return null;
  const hasLineDetails = cycles.some((c) => (pick(c, 'lines') || []).length > 0);
  const defaultExpandedMonth = cycles.length <= 12;
  const cliCycleCols = buildClientCycleColumnsWithNames(pick(month, 'clientsWhereClientPays'));
  const cliNameByCase = clientSummaryNameByCaseId(pick(month, 'clientsWhereClientPays'));
  const totalCharges = cycles.reduce((s, c) => s + (Number(pick(c, 'totalCharges')) || 0), 0);
  return (
    <Accordion defaultExpanded={defaultExpandedMonth} sx={{ mb: 0.5, '&:before': { display: 'none' }, '& .MuiAccordionDetails-root': { px: 1.5, pb: 1.5 } }} elevation={1}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 40, '& .MuiAccordionSummary-content': { my: 0.25, alignItems: 'center', gap: 1, flexWrap: 'wrap' } }}>
        <Typography sx={{ fontWeight: 700, fontSize: 13, minWidth: 70 }}>{formatMonthKey(pick(month, 'monthKey'))}</Typography>
        <Typography sx={{ fontSize: 11, color: '#666' }}>לקוחות משלמים: <strong>{cycles.length}</strong></Typography>
        <Typography sx={{ fontSize: 11, color: '#666' }}>סה״כ חיובים: <strong>{formatCurrency(totalCharges)}</strong></Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ pt: 0 }}>
        <CompactTable rows={cycles} columns={cliCycleCols} title={`מחזורי חיוב – לקוח משלם (${cycles.length})`} pageSize={cycles.length > 25 ? 25 : 10} />
        {hasLineDetails && (
          <Box sx={{ mt: 1.5 }}>
            <Typography variant="subtitle2" sx={{ fontSize: 11, fontWeight: 700, mb: 0.5 }}>פירוט לפי עסק</Typography>
            {cycles.map((cycle, idx) => {
              const caseId = pick(cycle, 'clientCaseId');
              const name = cliNameByCase[caseId] || pick(cycle, 'metadata')?.name;
              const lines = pick(cycle, 'lines') || [];
              return (
                <Accordion
                  key={pick(cycle, 'cycleId') || idx}
                  defaultExpanded={idx < CLIENT_DETAIL_INITIAL_EXPANDED}
                  sx={{ mb: 0.5, '&:before': { display: 'none' }, '& .MuiAccordionDetails-root': { py: 0, px: 1 } }}
                  elevation={0}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ fontSize: 18 }} />} sx={{ minHeight: 36, '& .MuiAccordionSummary-content': { my: 0.25, alignItems: 'center', flexWrap: 'wrap', gap: 0.5 } }}>
                    <Typography sx={{ fontSize: 11, fontWeight: 600, color: '#555', mr: 1 }}>
                      מזהה תיק: {caseId}{name ? ` · ${name}` : ''}
                    </Typography>
                    <Typography sx={{ fontSize: 11, color: '#666', direction: 'ltr' }}>{formatCurrency(pick(cycle, 'totalCharges'))}</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    {lines.length > 0 && <CompactTable rows={lines} columns={lineColumns} title="" pageSize={5} />}
                  </AccordionDetails>
                </Accordion>
              );
            })}
          </Box>
        )}
      </AccordionDetails>
    </Accordion>
  );
};

const GrandTotals = ({ totals, title = 'סיכום כללי לתקופה' }) => {
  if (!totals) return null;
  return (
    <Paper elevation={2} sx={{ p: 1.5, mt: 1.5, borderTop: '3px solid #1976d2' }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1, fontSize: 13 }}>{title}</Typography>
      <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
        <SummaryTile label="סה״כ חיובים" value={pick(totals, 'totalCharges')} />
        <SummaryTile label="חד פעמי" value={pick(totals, 'totalOneTimeCharges')} />
        <SummaryTile label="החזרים" value={pick(totals, 'totalRefunds')} />
        <SummaryTile label="הנחות" value={pick(totals, 'totalDiscounts')} />
        <SummaryTile label="תשלומים" value={pick(totals, 'totalPayments')} />
        <SummaryTile label="יתרה נטו" value={pick(totals, 'netBalance')} color={getNetBalanceColor(pick(totals, 'netBalance'))} />
      </Box>
    </Paper>
  );
};

/* ───────────────────────── main panel ───────────────────────── */
const ChargesDetailsPanel = ({ calcData, userType }) => {
  const [subTab, setSubTab] = useState(0);

  const months = useMemo(() => {
    const list = pick(calcData || {}, 'months') || [];
    return [...list].sort((a, b) => String(pick(b, 'monthKey') || '').localeCompare(String(pick(a, 'monthKey') || '')));
  }, [calcData]);
  const totals = pick(calcData || {}, 'totals');
  const clientPayersTotals = pick(calcData || {}, 'clientPayersTotals');

  const hasClientPayerData = useMemo(
    () => months.some((m) => (pick(m, 'clientCyclesWhereClientIsPayer')?.length || 0) > 0),
    [months]
  );

  if (!months.length) {
    return <Alert severity="info" sx={{ mt: 1 }}>אין נתונים לתקופה שנבחרה</Alert>;
  }

  // Client (userType !== 2): single detailed list + grand totals
  if (userType !== 2) {
    return (
      <Box>
        {months.map((month, i) => (
          <MonthAccordion key={pick(month, 'monthKey') || i} month={month} showClientPayerSection={false} showClientCounts={false} />
        ))}
        <GrandTotals totals={totals} />
      </Box>
    );
  }

  // Accountant (userType === 2): two sub-tabs (accountant-payer / client-payer)
  return (
    <Box>
      <Tabs value={subTab} onChange={(_, v) => setSubTab(v)} sx={{ mb: 2, minHeight: 40, '& .MuiTab-root': { minHeight: 40, fontSize: 12, textTransform: 'none' } }}>
        <Tab label="רו״ח משלם" id="charges-acc" aria-controls="charges-acc-panel" />
        <Tab label="לקוח משלם" id="charges-cli" aria-controls="charges-cli-panel" />
      </Tabs>

      <div role="tabpanel" hidden={subTab !== 0} id="charges-acc-panel" aria-labelledby="charges-acc">
        {subTab === 0 && (
          <>
            {months.map((month, i) => (
              <MonthAccordion key={pick(month, 'monthKey') || i} month={month} showClientPayerSection={false} showClientCounts />
            ))}
            <GrandTotals totals={totals} />
          </>
        )}
      </div>

      <div role="tabpanel" hidden={subTab !== 1} id="charges-cli-panel" aria-labelledby="charges-cli">
        {subTab === 1 && (
          <>
            {clientPayersTotals && (
              <Paper elevation={1} sx={{ p: 1.5, mb: 2, borderTop: '2px solid #ed6c02' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, fontSize: 12 }}>סיכום לקוחות משלמים בעצמם</Typography>
                <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                  <SummaryTile label="סה״כ חיובים" value={pick(clientPayersTotals, 'totalCharges')} />
                  <SummaryTile label="חד פעמי" value={pick(clientPayersTotals, 'totalOneTimeCharges')} />
                  <SummaryTile label="החזרים" value={pick(clientPayersTotals, 'totalRefunds')} />
                  <SummaryTile label="הנחות" value={pick(clientPayersTotals, 'totalDiscounts')} />
                  <SummaryTile label="תשלומים" value={pick(clientPayersTotals, 'totalPayments')} />
                  <SummaryTile label="יתרה" value={pick(clientPayersTotals, 'netBalance')} color={getNetBalanceColor(pick(clientPayersTotals, 'netBalance'))} />
                </Box>
              </Paper>
            )}
            {months.map((month, i) => (
              <ClientPayerMonthAccordion key={pick(month, 'monthKey') || i} month={month} />
            ))}
            {clientPayersTotals && <GrandTotals totals={clientPayersTotals} />}
            {!hasClientPayerData && (
              <Alert severity="info" sx={{ mt: 2 }}>אין מחזורי חיוב שבהם הלקוח משלם בעצמו בתקופה זו</Alert>
            )}
          </>
        )}
      </div>
    </Box>
  );
};

ChargesDetailsPanel.propTypes = {
  calcData: PropTypes.object,
  userType: PropTypes.number
};

export default ChargesDetailsPanel;
